import {useEffect, useLayoutEffect, useState} from 'react';
import api from '../../api';

const fields='input:not([type=hidden]):not([type=button]):not([type=submit]),select,textarea';
const valueOf=el=>el.type==='checkbox'||el.type==='radio'?el.checked:el.type==='file'?[...(el.files||[])].map(f=>`${f.name}:${f.size}:${f.lastModified}`).join('|'):el.value;
const ignored=el=>el.closest('[data-admin-ignore-draft]')||el.type==='search'||/بحث|ابحث|search/i.test(el.placeholder||'');

// UI-only draft tracking. Neither request bodies nor API paths/headers are changed.
// Retained panels own their React state; drafts never go to localStorage.
export default function useAdminDrafts(rootRef, active) {
 const [dirty,setDirty]=useState({});
 const [loading,setLoading]=useState(0);
 useLayoutEffect(()=>{
  const root=rootRef.current;if(!root)return;
  const records=new Map(),requests=new Map(),loads=new Set();let action=null,seq=0,disposed=false;
  const publish=()=>{
   if(disposed)return;
   const next={};
   for(const [el,r] of records){
    if(!el.isConnected){records.delete(el);continue;}
    if(r.settleVersion===r.version)r.base=valueOf(el);
    if(valueOf(el)!==r.base)next[r.section]=(next[r.section]||0)+1;
   }
   setDirty(old=>JSON.stringify(old)===JSON.stringify(next)?old:next);
  };
  const remember=el=>{
   if(!el?.matches?.(fields)||ignored(el)||!el.closest('[data-admin-section]'))return;
   if(!records.has(el))records.set(el,{base:valueOf(el),version:0,section:el.closest('[data-admin-section]').dataset.adminSection});
  };
  const focus=e=>remember(e.target);
  const schedulePublish=()=>setTimeout(publish,0);
  const input=e=>{remember(e.target);const row=records.get(e.target);if(row){row.version++;delete row.settleVersion;}schedulePublish();};
  const groupFor=el=>{
   const form=el.closest('form');if(form)return form;
   const tool=el.closest('[data-admin-tool]');
   for(let node=el.parentElement;node&&tool?.contains(node);node=node.parentElement){
    if(node.querySelector(fields))return node;
   }
   return tool;
  };
  const captureAction=e=>{
   const button=e.target.closest?.('button,input[type=submit]');
   if(!button)return;
   action={at:Date.now(),group:groupFor(button),save:/حفظ|اعتماد|تطبيق|إضافة|منح|تأكيد/.test(button.textContent||button.value||'')&&!/رفع/.test(button.textContent||'')};
  };
  const submit=e=>{action={at:Date.now(),group:e.target,save:true};};
  const observer=new MutationObserver(schedulePublish);
  observer.observe(root,{childList:true,subtree:true,attributes:true,attributeFilter:['value','checked']});
  root.addEventListener('focusin',focus);root.addEventListener('pointerdown',focus,true);root.addEventListener('beforeinput',focus,true);
  root.addEventListener('input',input);root.addEventListener('change',input);root.addEventListener('click',captureAction,true);root.addEventListener('submit',submit,true);
  const requestId=api.interceptors.request.use(config=>{
   if((config.method||'get').toLowerCase()==='get'&&/^\/(admin\/|stats\/counts|matches\/(live|history)|clans|tournaments|leagues\/active|rank-system|users\/search|me\/challenges|draws\/current)/.test(config.url||'')){
    const id=++seq;loads.add(id);config.rivalsAdminLoadingId=id;if(!disposed)setLoading(loads.size);
   }
   if(action?.save&&Date.now()-action.at<1500&&['post','put','patch'].includes((config.method||'').toLowerCase())&&action.group){
    const snapshots=[...records].filter(([el])=>action.group.contains(el)).map(([el,row])=>[el,valueOf(el),row.version]);
    const id=++seq;requests.set(id,snapshots);config.rivalsAdminDraftRequest=id;
   }
   return config;
  });
  const finishLoad=config=>{if(loads.delete(config?.rivalsAdminLoadingId)&&!disposed)setLoading(loads.size);};
  const responseId=api.interceptors.response.use(response=>{
   finishLoad(response.config);
   const id=response.config?.rivalsAdminDraftRequest;
   if(id&&requests.has(id)){
    for(const [el,value,version] of requests.get(id)){
     const row=records.get(el);if(!row)continue;row.base=value;
     // A successful form can clear its inputs. Accept that programmatic reset,
     // but keep edits typed while the request was in flight marked as dirty.
     if(row.version===version)row.settleVersion=version;
    }
    requests.delete(id);queueMicrotask(publish);
   }
   return response;
  },error=>{finishLoad(error.config);requests.delete(error.config?.rivalsAdminDraftRequest);return Promise.reject(error);});
  const count=(section)=>[...records].filter(([el,r])=>el.isConnected&&(!section||r.section===section)&&valueOf(el)!==r.base).length;
  const warning='لديك تعديلات غير محفوظة. تبقى القيم عند التنقل داخل لوحة الإدارة، وقد تفقدها عند مغادرتها أو تحديث الصفحة. هل تريد المتابعة؟';
  const click=e=>{
   const cancel=e.target.closest?.('button');
   if(cancel&&root.contains(cancel)&&cancel.type!=='submit'&&(/إلغاء|الغاء|إغلاق|اغلاق/.test(cancel.textContent||'')||cancel.querySelector('svg.lucide-x'))){
    const form=cancel.closest('form');
    if(form&&[...records].some(([el,r])=>form.contains(el)&&valueOf(el)!==r.base)&&!window.confirm('توجد تعديلات غير محفوظة في هذا النموذج. هل تريد إغلاقه دون حفظ؟')){e.preventDefault();e.stopPropagation();return;}
   }
   const link=e.target.closest?.('a[href]');if(!link||e.defaultPrevented||e.button!==0||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey||link.target==='_blank'||link.hasAttribute('download'))return;
   const destination=new URL(link.href,window.location.href),current=new URL(window.location.href);
   if(destination.href===current.href)return;
   const inside=destination.origin===current.origin&&destination.pathname===current.pathname;
   if(inside&&destination.searchParams.get('section')===current.searchParams.get('section'))return;
   const currentSection=root.dataset.activeSection;
   if(count(inside?currentSection:null)&&!window.confirm(warning)){e.preventDefault();e.stopPropagation();}
  };
  const unload=e=>{if(count()){e.preventDefault();e.returnValue='';}};
  // A capturing listener can cancel a browser Back before the router processes it.
  let lastHref=window.location.href,lastIndex=window.history.state?.idx,restoring=false;
  const pop=e=>{
   if(restoring){restoring=false;lastHref=window.location.href;lastIndex=window.history.state?.idx;return;}
   const old=new URL(lastHref),next=new URL(window.location.href),inside=old.pathname===next.pathname;
   if(count(inside?root.dataset.activeSection:null)&&!window.confirm(warning)){
    const diff=Number(lastIndex)-Number(e.state?.idx);
    if(Number.isFinite(diff)&&diff){e.stopImmediatePropagation();restoring=true;window.history.go(diff);return;}
   }
   lastHref=window.location.href;lastIndex=window.history.state?.idx;
  };
  document.addEventListener('click',click,true);window.addEventListener('beforeunload',unload);window.addEventListener('popstate',pop,true);
  // pushState/replaceState are observed through React's section render below.
  const route=()=>{lastHref=window.location.href;lastIndex=window.history.state?.idx;};
  root.addEventListener('admin-route-settled',route);
  return ()=>{
   disposed=true;observer.disconnect();api.interceptors.request.eject(requestId);api.interceptors.response.eject(responseId);
   root.removeEventListener('focusin',focus);root.removeEventListener('pointerdown',focus,true);root.removeEventListener('beforeinput',focus,true);
   root.removeEventListener('input',input);root.removeEventListener('change',input);root.removeEventListener('click',captureAction,true);root.removeEventListener('submit',submit,true);
   document.removeEventListener('click',click,true);window.removeEventListener('beforeunload',unload);window.removeEventListener('popstate',pop,true);root.removeEventListener('admin-route-settled',route);
  };
 },[rootRef]);
 useEffect(()=>{rootRef.current?.dispatchEvent(new Event('admin-route-settled'));},[rootRef,active]);
 return {dirty,loading};
}
